import { CalendarClock, Gavel } from 'lucide-react';

import type { Processo } from '@/api/client';
import { CORES_DO_VENCIMENTO } from '@/components/comunicacoes/SeloDeVencimento';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toLocalISODate } from '@/lib/formatters';
import { rotuloDoVencimento } from '@/lib/prazos/vencimento';
import { cn } from '@/lib/utils';

/**
 * Os chips de vencimento e audiência do Acervo (docs/PLANO_MOLDURA.md, Bloco 3), à
 * direita do cabeçalho do processo, junto das tags. O vencimento usa o rótulo e as cores
 * do selo das intimações — "Vence qui., ~22/10" —, com o vencido da semana em vermelho;
 * a audiência, a data e a hora quando a agenda as conhece. O rótulo longo vai na dica.
 */
export function VencimentoNaLista({ vencimento }: { vencimento: Processo['vencimento'] }) {
  if (!vencimento) return null;
  const rotulo = rotuloDoVencimento(vencimento.data, vencimento.estimado);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={cn(
            'shrink-0 cursor-default gap-1 whitespace-nowrap text-[11px] font-normal',
            CORES_DO_VENCIMENTO[rotulo.tom],
            vencimento.estimado && 'border-dashed',
          )}
        >
          <CalendarClock className="h-3 w-3" aria-hidden />
          {rotulo.curto}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{rotulo.longo}</TooltipContent>
    </Tooltip>
  );
}

const UM_DIA = 86_400_000;

function semHora(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** "Hoje, 14h", "Amanhã, 9h30", "05/11, 14h" ou só "05/11/2027" (hora desconhecida, outro ano). */
export function quandoDaAudiencia(inicio: string, horaConhecida: boolean, agora: Date = new Date()): string {
  const d = new Date(inicio);
  const dias = Math.round((semHora(d) - semHora(agora)) / UM_DIA);
  const dia =
    dias === 0
      ? 'Hoje'
      : dias === 1
        ? 'Amanhã'
        : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}${
            d.getFullYear() !== agora.getFullYear() ? `/${d.getFullYear()}` : ''
          }`;
  if (!horaConhecida) return dia;
  const hora = d.getMinutes() === 0 ? `${d.getHours()}h` : `${d.getHours()}h${String(d.getMinutes()).padStart(2, '0')}`;
  return `${dia}, ${hora}`;
}

export function AudienciaNaLista({ audiencia }: { audiencia: Processo['audiencia'] }) {
  if (!audiencia) return null;
  const tipo = audiencia.tipo === 'sessao' ? 'Sessão de julgamento' : 'Audiência';
  const quando = quandoDaAudiencia(audiencia.data_inicio, audiencia.hora_conhecida);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className="shrink-0 cursor-default gap-1 whitespace-nowrap text-[11px] font-normal text-foreground"
        >
          <Gavel className="h-3 w-3 text-muted-foreground" aria-hidden />
          <span className="sr-only">{tipo}: </span>
          {quando}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        {tipo}: {quando}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * O dia do próximo compromisso do processo, de hoje em diante — o prazo ou a audiência, o
 * que vier antes (YYYY-MM-DD) —, para ordenar o Acervo. O prazo que já venceu não conta:
 * o processo vai para depois dos que têm compromisso pela frente. Nenhum: `undefined`.
 */
export function dataDoCompromisso(
  processo: Pick<Processo, 'vencimento' | 'audiencia'>,
  hoje: string,
): string | undefined {
  const v = processo.vencimento;
  // Sem ``proximo`` (cache de antes do campo): a data, se ainda não venceu.
  const prazo = v ? (v.proximo !== undefined ? (v.proximo ?? undefined) : v.data >= hoje ? v.data : undefined) : undefined;
  const audiencia = processo.audiencia ? toLocalISODate(processo.audiencia.data_inicio) : undefined;
  if (!prazo) return audiencia;
  if (!audiencia) return prazo;
  return audiencia < prazo ? audiencia : prazo;
}
